import React, { useEffect, useMemo, useState } from "react";
import { Card, Select, Tabs } from "antd";
import Table from "../../../core/common/dataTable/index";
import { useDispatch, useSelector } from "react-redux";
import { fetchHierarchyLeaveRequests } from "../../../core/data/redux/attendance/leave/leaveAction";
import { getEmployeeByUserId, getEmployeeDetailsByManagerOrHod, hierarchyForSupervisorAndHod } from "../../../core/data/redux/employee/employeeAction";
import { AppDispatch } from "../../../core/data/redux/store";
import { filterData } from '../../../core/common/GlobalFunctions/tableFilters';
import { Link } from "react-router-dom";
import { useAppConfig } from "../../../customhooks/useAppConfig";
import FilterHeaderResign from "./FilterHeaderResign";
import FilterHeaderEmployee from "./FilterHeader";
import { filterByStatus } from "../../../core/utils/filterActiveData";
import { fetchAllDepartments } from "../../../core/data/redux/department/departmentAction";
import { fetchAllDesignations } from "../../../core/data/redux/designation/designationAction";
import { fetchHierarchyResignationRequests, updateResignationStatus } from "../../../core/data/redux/resignation/resignationAction";
import TeamHierarchy from "./TeamsHierarchy";

const { TabPane } = Tabs;

const EmployeeDashTeam: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { leaveRequestsByHodAndSupervisor } = useSelector((state: any) => state.attendance.leaveRequests)
  const { user } = useSelector((state: any) => state.auth);
  const { employeesbyUser } = useSelector((state: any) => state.employee);
  const employeeId = employeesbyUser?.employeeId;
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedSort, setSelectedSort] = useState<any>("");
  const [tableKey, setTableKey] = useState(1);

  const EmployeeDetailsByManagerOrHod = useSelector((state: any) => state.employee.EmployeeDetailsByManagerOrHod);
  const { departments } = useSelector((state: any) => state.department);
  const { designations } = useSelector((state: any) => state.designation);
  const activeEmployeeDesignation = filterByStatus(designations, 'active', 'DesignationStatus');
  const activeDepartment = filterByStatus(departments, 'active', 'Status');
  const resignationRequest = useSelector((state: any) => state.resignation.resignationRequest);
  const [loadingIds, setLoadingIds] = useState<number[]>([]);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [selectedRoleFilter, setSelectedRoleFilter] = useState("all");

  useEffect(() => {
    dispatch(hierarchyForSupervisorAndHod(employeeId));
  }, [dispatch]);

  const getDepartmentName = (id: number) =>
    activeDepartment?.find((d: any) => d.Id === id)?.Name || "—";

  const getDesignationName = (id: number) =>
    activeEmployeeDesignation?.find((d: any) => d.DesignationId === id)?.DesignationName || "—";

  const statusColors: { [key: string]: string } = {
    Pending: "#f0ad4e",
    Approved: "#5cb85c",
    Rejected: "#d9534f",
  };

  useEffect(() => {
    if (!employeesbyUser?.employeeId && user?.UserId) { dispatch(getEmployeeByUserId({ userId: user.UserId })); }
  }, [dispatch, employeesbyUser, user]);

  useEffect(() => {
    if (employeeId) {
      dispatch(fetchHierarchyLeaveRequests({ useRouteId: employeeId, includeSelf: false }));
      dispatch(getEmployeeDetailsByManagerOrHod(employeeId));
      dispatch(fetchHierarchyResignationRequests({ useRouteId: employeeId, includeSelf: false }));
    }
  }, [dispatch, employeeId]);

  useEffect(() => {
    dispatch(fetchAllDepartments());
    dispatch(fetchAllDesignations());
  }, [dispatch]);

  const filteredHierarchyLeaves = useMemo(() => {
    const filters = selectedStatus && selectedStatus !== "All" ? { LeaveRequestStatus: selectedStatus } : {};
    return filterData(leaveRequestsByHodAndSupervisor, filters, "CreatedAt", "CreatedAt", selectedSort);
  }, [leaveRequestsByHodAndSupervisor, selectedStatus, selectedSort]);

  const filteredResignation = useMemo(() => {
    const filters = selectedStatus && selectedStatus !== "All" ? { ResignationStatus: selectedStatus } : {};
    return filterData(resignationRequest, filters, "CreatedAt", "CreatedAt", selectedSort);
  }, [resignationRequest, selectedStatus, selectedSort]);

  const filteredEmployees = useMemo(() => {
    let list = EmployeeDetailsByManagerOrHod?.employees || [];

    if (selectedRoleFilter === "supervisor") list = list.filter((e: any) => e.SupervisorId === employeeId);
    if (selectedRoleFilter === "hod") list = list.filter((e: any) => e.hod_id === employeeId);
    if (selectedStatus !== "All") { list = list.filter((emp: any) => emp.employee_status?.toLowerCase() === selectedStatus.toLowerCase()) }

    return filterData(list, {}, "CreatedAt", "CreatedAt", selectedSort);
  }, [EmployeeDetailsByManagerOrHod, selectedRoleFilter, selectedStatus, selectedSort, employeeId]);

  useEffect(() => setTableKey(prev => prev + 1), [filteredHierarchyLeaves]);
  useEffect(() => setTableKey(prev => prev + 1), [filteredResignation]);
  useEffect(() => setTableKey(prev => prev + 1), [filteredEmployees]);

  const getStatusField = (record: any) => {
    const isManager = record.Manager == employeeId;
    const isHOD = record.hod_id === employeeId;

    if (isManager) return "manager_status";
    if (isHOD) return "hod_status";
    return null;
  };

  const getFinalStatus = (record: any) => {
    const { hod_status, hr_status, manager_status } = record;

    if ([hod_status, hr_status, manager_status].includes("Rejected")) {
      return "Rejected";
    }

    if (
      hod_status === "Approved" &&
      hr_status === "Approved" &&
      manager_status === "Approved"
    ) {
      return "Approved";
    }

    return "Pending";
  };

  const updateStatus = async (status: string, record: any, field: string) => {
    setLoadingIds((prev) => [...prev, record.Id]);
    setIsStatusUpdating(true);

    const payload = {
      resignationId: record.Id,
      status,
      field,
      ModifiedBy: employeeId,
    };

    await dispatch(updateResignationStatus(payload));

    await dispatch(fetchHierarchyResignationRequests({ useRouteId: employeeId, includeSelf: false }));

    setLoadingIds((prev) => prev.filter((id) => id !== record.Id));
    setIsStatusUpdating(false);
  };


  const resignationColumns = [
    {
      title: "Resignation ID",
      dataIndex: "Id",
      render: (text: string, record: any) => (
        <Link to="#" className="ps-4 fw-medium text-dark">
          {record.Id || "N/A"}
        </Link>
      ),
      sorter: (a: any, b: any) => a.Id - b.Id,
    },
    {
      title: "Employee Name",
      dataIndex: "EmployeeName",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center">
          <Link to="#" className="avatar avatar-md">
            <img
              src={
                record.PictureURL ??
                "https://cdn-icons-png.flaticon.com/512/149/149071.png"
              }
              alt="Employee"
              className="img-fluid rounded-circle"
            />
          </Link>
          <div className="ms-2">
            <Link to="#" className="fw-semibold">
              {record.EmployeeName || "N/A"}
            </Link>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) =>
        a.EmployeeName.localeCompare(b.EmployeeName),
    },
    {
      title: "Department",
      dataIndex: "DepartmentName",
      render: (text: string) => text || "—",
      sorter: (a: any, b: any) =>
        (a.DepartmentName || "").localeCompare(b.DepartmentName || ""),
    },
    {
      title: "Designation",
      dataIndex: "DesignationName",
      render: (text: string) => text || "—",
      sorter: (a: any, b: any) =>
        (a.DesignationName || "").localeCompare(b.DesignationName || ""),
    },
    {
      title: "Resignation Date",
      dataIndex: "ResignationDate",
      render: (text: string) =>
        text ? new Date(text).toLocaleDateString() : "—",
      sorter: (a: any, b: any) =>
        new Date(a.ResignationDate).getTime() -
        new Date(b.ResignationDate).getTime(),
    },
    {
      title: "Notice Date",
      dataIndex: "NoticeDate",
      render: (text: string) =>
        text ? new Date(text).toLocaleDateString() : "—",
      sorter: (a: any, b: any) =>
        new Date(a.NoticeDate).getTime() -
        new Date(b.NoticeDate).getTime(),
    },
    {
      title: "Status",
      dataIndex: "hod_status",
      render: (text: string, record: any) => {
        const field = getStatusField(record);
        if (!field) return "N/A";
        const currentStatus = record[field];
        const hodBlocked = field === "hod_status" && record.manager_status !== "Approved";
        return (
          <Select
            value={currentStatus}
            onChange={(value) => updateStatus(value, record, field)}
            style={{ width: 120 }}
            size="small"
            disabled={
              currentStatus === "Approved" ||
              loadingIds.includes(record.Id) ||
              isStatusUpdating ||
              hodBlocked
            }
          >
            {["Pending", "Approved", "Rejected"]?.map((status) => (
              <Select.Option key={status} value={status}>
                <span style={{ color: statusColors[status], fontWeight: 500 }}>
                  {status}
                </span>
              </Select.Option>
            ))}
          </Select>
        );
      }
    },
    {
      title: "Final Status",
      dataIndex: "ResignationStatus",
      render: (text: string, record: any) => {
        const finalStatus = getFinalStatus(record);

        return (
          <Select
            value={finalStatus}
            style={{ width: 120 }}
            size="small"
            disabled   // Always readonly
          >
            {["Pending", "Approved", "Rejected"].map((status) => (
              <Select.Option key={status} value={status}>
                <span
                  style={{
                    color:
                      status === "Pending"
                        ? "#faad14"
                        : status === "Approved"
                          ? "#52c41a"
                          : "#ff4d4f",
                    fontWeight: 600,
                  }}
                >
                  {status}
                </span>
              </Select.Option>
            ))}
          </Select>
        );
      },
    },
  ];
  const employeeColumns = [
    {
      title: "Emp ID",
      dataIndex: "EmployeeNumber",
      key: "EmployeeNumber",
    },
    {
      title: "Employee Name",
      dataIndex: "Name",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center">
          <Link to="#" className="avatar avatar-md">
            <img
              src={
                record.PictureURL ??
                "https://cdn-icons-png.flaticon.com/512/149/149071.png"
              }
              alt="Employee"
              className="img-fluid rounded-circle"
            />
          </Link>
          <div className="ms-2">
            <Link to="#" className="fw-semibold">
              {record.Name || "N/A"}
            </Link>
            <div className="text-muted fs-12">{record.role || ""}</div>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => a.Name.localeCompare(b.Name),
    },
    {
      title: "Department",
      dataIndex: "DepartmentId",
      key: "DepartmentId",
      render: (value: any) => getDepartmentName(value),
    },
    {
      title: "Designation",
      dataIndex: "DesignationId",
      key: "DesignationId",
      render: (value: any) => getDesignationName(value),
    },
    {
      title: "Date of Joining",
      dataIndex: "DateOfJoining",
      key: "DateOfJoining",
      render: (date: string) =>
        date ? new Date(date).toLocaleDateString("en-IN") : "—",
    },
    {
      title: "Status",
      dataIndex: "employee_status",
      key: "employee_status",
      render: (status: string) => (
        <span
          style={{
            color: status === "active" ? "green" : "red",
            fontWeight: 600,
          }}
        >
          {status?.toUpperCase()}
        </span>
      ),
    },
  ];

  const employeeRoleOptions = useMemo(() => {
    if (!EmployeeDetailsByManagerOrHod?.employees) return [];

    const list = [{ key: "all", label: "All Employees", id: null }];

    const supervisorEmployees = EmployeeDetailsByManagerOrHod.employees.filter((e: any) => e.SupervisorId === employeeId);
    if (supervisorEmployees.length > 0) list.push({ key: "supervisor", label: "Supervisor", id: employeeId });

    const hodEmployees = EmployeeDetailsByManagerOrHod.employees.filter((e: any) => e.hod_id === employeeId);
    if (hodEmployees.length > 0) { list.push({ key: "hod", label: "HOD", id: employeeId }) }

    return list;
  }, [EmployeeDetailsByManagerOrHod, employeeId]);

  const config = useAppConfig();
  if (!config) return null;

  return (
    <div className="team-dashboard">
      <Tabs
        defaultActiveKey="1"
        type="card"
        size="large"
        className="mb-4"
        tabBarGutter={8}
      >
        {/*Team Overview */}
        <TabPane tab="👤 Team Overview" key="1">
          <div className="row mb-4">
            <div className="row">
              <div className="col-md-4 col-12">
                <Card className="text-center">
                  <h6>Total Employees</h6>
                  <h3>{EmployeeDetailsByManagerOrHod?.stats?.totalActive || 0}</h3>
                </Card>
              </div>
              <div className="col-md-4 col-12">
                <Card className="text-center">
                  <h6>On Leave</h6>
                  <h3>{EmployeeDetailsByManagerOrHod?.stats?.onLeaveToday || 0}</h3>
                </Card>
              </div>
              <div className="col-md-4 col-12">
                <Card className="text-center">
                  <h6>New Joiners</h6>
                  <h3>{EmployeeDetailsByManagerOrHod?.stats?.newJoiners || 0}</h3>
                </Card>
              </div>
            </div>
          </div>
          <div className="card">
            <FilterHeaderEmployee
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              selectedSort={selectedSort}
              setSelectedSort={setSelectedSort}
              employeeRoleOptions={employeeRoleOptions}
              selectedRoleFilter={selectedRoleFilter}
              setSelectedRoleFilter={setSelectedRoleFilter}
            />
            <div className="card-body p-0">
              <Table
                key={`hierarchy-${tableKey}`}
                dataSource={filteredEmployees}
                columns={employeeColumns}
                Selection={false}
              />
              <div className="card-body p-0">
              </div>
            </div>
          </div>
        </TabPane>

        {/*  📄 Resignation Requests */}
        <TabPane tab=" 📄 Resignation Requests" key="2">
          <div className="row mb-4">
            {/* {getStatusCards(resignationRequest, "LeaveRequestStatus")} */}
          </div>
          <div className="card">
            <FilterHeaderResign
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              selectedSort={selectedSort}
              setSelectedSort={setSelectedSort}
            />
            <div className="card-body p-0">
              <Table
                key={`hierarchy-${tableKey}`}
                dataSource={filteredResignation}
                columns={resignationColumns}
              // Selection={false}
              />
            </div>
          </div>
        </TabPane>

        <TabPane tab="👥 Teams Hierarchy" key="3">
          <TeamHierarchy />
        </TabPane>

        {/* 🎯 Performance */}
        {/* <TabPane tab="🎯 Performance & Productivity" key="4">
          <div className="row g-3">
            <div className="col-md-3">
              <Card className="shadow-sm border-0">
                <h6>Goal Achievement Progress</h6>
                <h3>78%</h3>
                <p className="small text-muted">+6% vs last month</p>
              </Card>
            </div>
            <div className="col-md-3">
              <Card className="shadow-sm border-0">
                <h6>Performance Distribution</h6>
                <h3>High: 40%</h3>
                <p className="small text-muted">Low: 15% • Mid: 45%</p>
              </Card>
            </div>
            <div className="col-md-3">
              <Card className="shadow-sm border-0">
                <h6>Task / Project Completion</h6>
                <h3>91%</h3>
                <p className="small text-muted">Overdue: 4%</p>
              </Card>
            </div>
            <div className="col-md-3">
              <Card className="shadow-sm border-0">
                <h6>Productivity Index</h6>
                <h3>8.5 / 10</h3>
                <p className="small text-muted">Improving Trend ↑</p>
              </Card>
            </div>
          </div>
        </TabPane> */}

        {/* 📅 Engagement */}
        {/* <TabPane tab="📅 Engagement & Feedback" key="5">
          <div className="row g-3">
            <div className="col-md-3">
              <Card className="shadow-sm border-0">
                <h6>Team Pulse Sentiment</h6>
                <h3>78% Positive</h3>
                <p className="small text-muted">Neutral: 15% • Negative: 7%</p>
              </Card>
            </div>
            <div className="col-md-3">
              <Card className="shadow-sm border-0">
                <h6>1:1 Meeting Tracker</h6>
                <h3>87%</h3>
                <p className="small text-muted">Avg Interval: 14 days</p>
              </Card>
            </div>
            <div className="col-md-3">
              <Card className="shadow-sm border-0">
                <h6>Recognition Feed</h6>
                <h3>12 Kudos</h3>
                <p className="small text-muted">This Quarter</p>
              </Card>
            </div>
            <div className="col-md-3">
              <Card className="shadow-sm border-0">
                <h6>Engagement Survey</h6>
                <h3>88% Response</h3>
                <p className="small text-muted">Satisfaction Index: 8.2</p>
              </Card>
            </div>
          </div>
        </TabPane> */}

        {/* 💼 Compliance */}
        {/* <TabPane tab="💼 Compliance" key="5">
          <div className="row g-3">
            <div className="col-md-3">
              <Card className="shadow-sm border-0">
                <h6>Policy Compliance</h6>
                <h3>94%</h3>
                <p className="small text-muted">2 Pending Acks</p>
              </Card>
            </div>
            <div className="col-md-3">
              <Card className="shadow-sm border-0">
                <h6>Probation Summary</h6>
                <h3>3 Pending</h3>
                <p className="small text-muted">Due this month</p>
              </Card>
            </div>
            <div className="col-md-3">
              <Card className="shadow-sm border-0">
                <h6>Training Completion</h6>
                <h3>92%</h3>
                <p className="small text-muted">Overdue: 1</p>
              </Card>
            </div>
            <div className="col-md-3">
              <Card className="shadow-sm border-0">
                <h6>Incident Summary</h6>
                <h3>1 Open</h3>
                <p className="small text-muted">Resolved: 5</p>
              </Card>
            </div>
          </div>
        </TabPane> */}

        {/* 🚨 Alerts */}
        {/* <TabPane tab="🚨 Leave Requests" key="6">
          <div className="row mb-4">
            {getStatusCards(leaveRequestsByHodAndSupervisor, "LeaveRequestStatus")}
          </div>
          <div className="card">
            <FilterHeader
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              selectedSort={selectedSort}
              setSelectedSort={setSelectedSort}
            />
            <div className="card-body p-0">
              <Table
                key={`hierarchy-${tableKey}`}
                dataSource={filteredHierarchyLeaves}
                columns={leaveColumns}
              // Selection={false}
              />
            </div>
          </div>
        </TabPane> */}


        {/* 📈 Strategic Insights */}
        {/* <TabPane tab="📈 Strategic Insights" key="7">
          <div className="row g-3">
            <div className="col-md-3">
              <Card className="shadow-sm border-0">
                <h6>Attrition Risk Radar</h6>
                <h3>8%</h3>
                <p className="small text-muted">Stable trend</p>
              </Card>
            </div>
            <div className="col-md-3">
              <Card className="shadow-sm border-0">
                <h6>Succession Readiness</h6>
                <h3>72%</h3>
                <p className="small text-muted">Coverage improved</p>
              </Card>
            </div>
            <div className="col-md-3">
              <Card className="shadow-sm border-0">
                <h6>Diversity Mix</h6>
                <h3>45% Female</h3>
                <p className="small text-muted">Avg Exp: 4.1 yrs</p>
              </Card>
            </div>
            <div className="col-md-3">
              <Card className="shadow-sm border-0">
                <h6>Learning Impact</h6>
                <h3>+12%</h3>
                <p className="small text-muted">Post-training improvement</p>
              </Card>
            </div>
          </div>
        </TabPane> */}
      </Tabs>
    </div>
  );
};

export default EmployeeDashTeam;
